import {
    CategoryScale,
    Chart,
    Colors,
    LinearScale,
    LineController,
    LineElement,
    BarController,
    BarElement,
    PointElement
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

Chart.register(annotationPlugin);

Chart.register(
    Colors,
    BarController,
    LineController,
    LineElement,
    BarElement,
    CategoryScale,
    PointElement,
    LinearScale,
);

export function createTempChart(canvas_ctx, data_min, data_max, data_values, alpha) {
    const labels = Array.from({length: 24}, (_, i) => i);
    const chartjs_data = {
        labels: labels,
        datasets: [
            {
                label: 'Temperature',
                data: data_values,
                pointRadius: 0,
                borderColor: 'rgba(0, 0, 0, 0)', // temporary, will be replaced later
                cubicInterpolationMode: 'monotone',
                borderWidth: 2
            },
        ]
    };

    const config = {
        type: 'line',
        data: chartjs_data,
        options: {
            animation: false,
            maintainAspectRatio: false,
            scales: {
                x: {
                    display: false
                },
                y: {
                    min: data_min,
                    max: data_max,
                    display: false
                }
            },
            plugins: {
                legend: {
                    display: false
                }
            }
        }
    };

    const chart = new Chart(canvas_ctx, config);

    // Now that the chart is constructed, you can access its context and chartArea
    const {ctx, chartArea} = chart;

    // Create the gradient
    // Apply the gradient to the border color
    chart.data.datasets[0].borderColor = createGradient(ctx, data_values, data_min, data_max, chartArea, alpha);

    // Update the chart to reflect the changes
    chart.update();
}


function createGradient(ctx, data, data_min, data_max, chartArea, alpha) {

    const tempColors = [
        {temp: -40, color: `rgba(23,128,217, ${alpha})`},    // Blue for cold
        {temp: 0, color: `rgba(86,179,255, ${alpha})`},      // Lighter Blue
        {temp: 10, color: `rgba(68, 159, 47, ${alpha})`},    // Greenish
        {temp: 18, color: `rgba(255,205,86, ${alpha})`},     // Yellow
        {temp: 28, color: `rgba(245,109,109, ${alpha})`},   // Red for hot
        {temp: 40, color: `rgba(220,52,116, ${alpha})`}     // Violet for very hot
    ];

    let gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

    tempColors.forEach((point) => {
        if (point.temp < data_min) {
            gradient.addColorStop(0, point.color);
        } else if (point.temp > data_max) {
            gradient.addColorStop(1, point.color);
        } else {
            let pos = (point.temp - data_min) / (data_max - data_min);
            gradient.addColorStop(pos, point.color);
        }
    });

    return gradient;
}

export function createPrecipChart(canvas_ctx, data_min, data_max, data_values, alpha) {
    const labels = Array.from({length: 24}, (_, i) => i);
    const chartjs_data = {
        labels: labels,
        datasets: [
            {
                label: 'Precipitation',
                data: data_values,
                pointRadius: 0,
                borderRadius: 3,
                barPercentage: 0.7,
                backgroundColor: `rgba(140, 160, 185, ${alpha})`
            },
        ]
    };

    const config = {
        type: 'bar',
        data: chartjs_data,
        options: {
            animation: false,
            maintainAspectRatio: false,
            scales: {
                x: {
                    display: false
                },
                y: {
                    min: data_min,
                    max: data_max,
                    display: false
                }
            },
            plugins: {
                legend: {
                    display: false
                }
            }
        }
    };

    const chart = new Chart(canvas_ctx, config);

    // Update the chart to reflect the changes
    chart.update();
}

function roundToNearest(actual_max) {
    if (actual_max > 1) {
        // Round to nearest 0.5
        return Math.round(actual_max * 2) / 2;
    } else {
        // Round to nearest 0.1
        return Math.round(actual_max * 10) / 10;
    }
}

export function createMinutePrecipChart(canvas_ctx, data_min, data_max, data_values, alpha) {
    let actual_max = roundToNearest(Math.max(...data_values.slice(0, 30)));
    const labels = Array.from({length: data_values.length}, (_, i) => i);
    const chartjs_data = {
        labels: labels,
        datasets: [
            {
                label: 'Precipitation',
                data: data_values,
                pointRadius: 0,
                barPercentage: 0.7,
                backgroundColor: `rgba(83,172,245, ${alpha})`
            },
        ]
    };

    const config = {
        type: 'bar',
        data: chartjs_data,
        options: {
            animation: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    display: false,
                    max: 30
                },
                y: {
                    min: data_min,
                    max: data_max,
                    display: false
                }
            },
            layout: {
                autoPadding: false
            },
            plugins: {
                legend: {
                    display: false
                },
                // Custom plugin to draw text on top of the max value bar
                annotation: {
                    annotations: [
                        {
                            type: 'label',
                            xValue: -1,
                            position: 'start',
                            yValue: Math.min(data_max, actual_max + 4.4),
                            content: [`${actual_max} mm`],
                            font: {
                                size: 18
                            },
                            textAlign: 'center',
                            color: '#FFFFFF'
                        },
                        {
                            type: 'line',
                            yMin: Math.min(data_max, actual_max + 0.3),
                            yMax: Math.min(data_max, actual_max + 0.3),
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            borderDash: [5, 2],
                            borderWidth: 1
                        }
                    ]
                }
            }
        }
    };

    const chart = new Chart(canvas_ctx, config);

    // Update the chart to reflect the changes
    chart.update();
    return chart;
}
